/**
 * JobsWatcher.tsx — app-wide analysis-completion notifier (2026-07-23 UX
 * decision: the user is free to navigate anywhere during the hours-long run;
 * when it finishes, a notification appears wherever they are).
 *
 * Mechanism: polls the org's active analyze jobs (GET /v1/jobs/active, 15s).
 * When a job that was active disappears from the list, it fetches that job's
 * terminal state and shows a dismissible toast — green "Analisi completata"
 * linking to the project's Audit page, or red "Analisi fallita" with the error.
 * Completion also invalidates the tender caches so every visible surface
 * (dashboard rows, stat cards, audit tree) refreshes itself.
 *
 * If the browser was closed when the run finished there is no toast — the
 * results are simply there on the next visit (the Audit page rediscovers the
 * latest job; the dashboard shows the updated project).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useApi } from "../../lib/api";
import { toAnalysisJob, useActiveJobs, type AnalysisJob, type JobDto } from "../../lib/tenderApi";

interface Toast {
  id: string;
  projectId: string;
  ok: boolean;
  text: string;
}

export function JobsWatcher(): JSX.Element | null {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const activeQuery = useActiveJobs();
  const known = useRef<Map<string, AnalysisJob>>(new Map());
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const current = activeQuery.data;
    if (!current) return;
    const currentIds = new Set(current.map((j) => j.id));

    // Jobs we saw active before that are no longer active → reached a terminal
    // state; fetch it and announce.
    for (const [id, prev] of known.current) {
      if (currentIds.has(id)) continue;
      known.current.delete(id);
      void (async () => {
        try {
          const dto = await api.cmeinsight.get<JobDto>(`/v1/jobs/${id}`);
          const job = toAnalysisJob(dto);
          const ok = job.status === "completed";
          if (ok) {
            void qc.invalidateQueries({ queryKey: ["tender"] });
          }
          setToasts((t) => [
            ...t,
            {
              id,
              projectId: job.projectId,
              ok,
              text: ok
                ? "Analisi completata — i risultati sono pronti."
                : `Analisi fallita${job.error ? `: ${job.error}` : "."}`,
            },
          ]);
        } catch {
          // If the terminal fetch fails, fall back to a neutral completion note.
          setToasts((t) => [
            ...t,
            {
              id,
              projectId: prev.projectId,
              ok: true,
              text: "Un'analisi è terminata — apri il progetto per i risultati.",
            },
          ]);
        }
      })();
    }
    for (const j of current) known.current.set(j.id, j);
  }, [activeQuery.data, api, qc]);

  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxWidth: 380,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="card"
          style={{
            padding: "12px 14px",
            borderLeft: `3px solid ${t.ok ? "var(--c-ok)" : "var(--c-err)"}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ flex: 1, fontSize: 12.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>
              {t.ok ? "Analisi completata ✓" : "Analisi non riuscita"}
            </div>
            <div style={{ color: "var(--c-ink-3)" }}>{t.text}</div>
            <button
              className="btn btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => {
                setToasts((x) => x.filter((y) => y.id !== t.id));
                navigate(`/audit/${t.projectId}`);
              }}
            >
              Apri il progetto
            </button>
          </div>
          <button
            className="btn btn-ghost btn-icon btn-sm"
            aria-label="Chiudi notifica"
            onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
